import { useState, useEffect } from "react";
import styles from "./list.module.css";
import { TextField, Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, TableFooter } from "@mui/material";
import { StyledMainTableCell, StyledMainTableRow } from "../../components/TableComponent";
import TablePaginationComponent from "../../components/TablePagination";
import { Add, Close } from "@mui/icons-material";
import axios from "axios";
import Loader from "../../components/Loader";
import { EmailTemplateListLoader } from "./loaders";
import { authData, withAdminAuth } from "../../components/GetAuth";
import { Edit, Eye, Home, Search } from "react-feather";
import { Link, useNavigate } from "react-router-dom";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";

const EmailTemplateList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterVars, setFilterVars] = useState({ searchKey: "" });
  const auth = authData();
  const API_TOKEN = auth?.api_token;

  const { websiteData } = useWebsite();
  useSiteBranding("Email templates", websiteData);

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchProducts = () => {
      setContentLoading(true);
      axios.get(process.env.REACT_APP_API_URL + "v1/email-templates", {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setContentLoading(false);
        setAllData(response.data.data);
      }).catch((error) => {
        setContentLoading(false);
      });
    };

    fetchProducts();
  }, [API_TOKEN]);

  useEffect(() => {
    let filterredTempData = allData;

    if (filterVars?.searchKey !== "") {
      let inputVal = filterVars?.searchKey;
      inputVal = inputVal.toLowerCase();
      filterredTempData = filterredTempData.filter((item) => {
        return item.title.toLowerCase().includes(inputVal);
      });
    }

    setFilteredData(filterredTempData);
  }, [allData, filterVars]);

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />
    <div className={styles.MainDiv}>
      <div className={styles.MainCard}>
        {contentLoading && <EmailTemplateListLoader />}

        {!contentLoading && (<div className={styles.PageTitleWrap}>
          <p className={styles.PageTitle}>Email Templates</p>
          <nav aria-label="breadcrumb">
            <ol className={styles.breadcrumb}>
              <li className={styles.breadcrumbItem}><span><Home /></span>Email Templates</li>
              <li className={`${styles.breadcrumbItem} ${styles.active}`}>List</li>
            </ol>
          </nav>
        </div>)}

        {!contentLoading && (<>
          <div className={styles.Toolbar}>
            <div className={styles.SearchBox}>
              <Search className={styles.SearchIcon} />
              <TextField
                id="search"
                type="text"
                placeholder="Search by title"
                aria-label="Search Input"
                className={styles.SearchInput}
                fullWidth
                size="small"
                value={filterVars?.searchKey}
                onChange={(e) =>
                  setFilterVars((prev) => {
                    return { ...prev, searchKey: e.target.value };
                  })
                }
              />
              {filterVars?.searchKey !== "" && (<Button
                type="button"
                className={styles.ClearButton}
                aria-label="Clear search"
                onClick={(e) =>
                  setFilterVars((prev) => {
                    return { ...prev, searchKey: "" };
                  })
                }
              >
                <Close fontSize="small" />
              </Button>)}
            </div>
            {auth?.user_type === 1 && (<Button component={Link} className={styles.AddButton} to={"/email-template/add"}>
              <Add fontSize="small" /> Email Template
            </Button>)}
          </div>

          <div className={styles.TableWrap}>
            <TableContainer component={Paper} elevation={0}>
              <Table className={styles.Table} aria-label="Email templates table">
                <TableHead>
                  <TableRow>
                    <StyledMainTableCell className={styles.TableHeadCell}>Title</StyledMainTableCell>
                    <StyledMainTableCell className={styles.TableHeadCell} align="right">
                      Actions
                    </StyledMainTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => {
                    return (<StyledMainTableRow key={index} className={styles.TableRow}>
                      <TableCell className={styles.TitleCell} data-label="Title">{item?.title}</TableCell>
                      <TableCell align="right">
                        <div className={styles.ActionsCell}>
                          <Button
                            className={styles.ActionButton}
                            aria-label="Preview template"
                            onClick={(e) => navigate("/email-template/preview/" + item?.id)}
                          >
                            <Eye />
                          </Button>
                          <Button
                            className={styles.ActionButton}
                            aria-label="Edit template"
                            onClick={(e) => navigate("/email-template/edit/" + item?.id)}
                          >
                            <Edit />
                          </Button>
                        </div>
                      </TableCell>
                    </StyledMainTableRow>);
                  })}
                  {filteredData.length === 0 && (<TableRow className={styles.EmptyRow}>
                    <TableCell colSpan={2} align="center">
                      No email templates found
                    </TableCell>
                  </TableRow>)}
                </TableBody>

                {filteredData.length > 0 && (<TableFooter>
                  <TableRow>
                    <TablePaginationComponent
                      count={filteredData.length}
                      colSpan={2}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={paginationChangePage}
                      handleChangeRowsPerPage={paginationChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>)}
              </Table>
            </TableContainer>
          </div>
        </>)}
      </div>
    </div>
  </>);
};

export default withAdminAuth(EmailTemplateList);